import axios from 'axios';

const INITIAL_STATE = {
  currentStep: 1, // 1=Decorate, 2=Naming, 3=Confirmation
  exportedImage: null,
  eggName: '',
  isSaving: false,
  saveError: null,
  saveResult: null,
  designs: [],
  designsLoading: false,
};

// Spreading INITIAL_STATE alone is a shallow copy, so every state instance would
// share the same `designs` array by reference — one store mutating it would show
// up in another. Give each instance its own.
const state = () => ({ ...INITIAL_STATE, designs: [] });

const mutations = {
  SET_CURRENT_STEP(state, step) {
    state.currentStep = step;
  },
  SET_EXPORTED_IMAGE(state, image) {
    state.exportedImage = image;
  },
  SET_SAVING(state, saving) {
    state.isSaving = saving;
  },
  SET_SAVE_ERROR(state, error) {
    state.saveError = error;
  },
  SET_SAVE_RESULT(state, result) {
    state.saveResult = result;
  },
  SET_EGG_NAME(state, name) {
    state.eggName = name;
  },
  UPDATE_DESIGN_MESSAGE(state, { id, message }) {
    const design = state.designs.find(d => d.id === id);
    if (design) design.message = message;
  },
  UPDATE_DESIGN_FEATURED(state, { id, featured }) {
    const design = state.designs.find(d => d.id === id);
    if (design) design.featured = featured;
  },
  RESET_STATE(state) {
    const { designs, designsLoading } = state;
    Object.assign(state, { ...INITIAL_STATE, designs, designsLoading });
  },
  SET_DESIGNS(state, designs) {
    state.designs = designs;
  },
  SET_DESIGNS_LOADING(state, loading) {
    state.designsLoading = loading;
  },
  REMOVE_DESIGN(state, id) {
    state.designs = state.designs.filter(d => d.id !== id);
  },
};

const actions = {
  async saveEgg({ commit, rootState, state }) {
    try {
      commit('SET_SAVING', true);
      commit('SET_SAVE_ERROR', null);

      const accessToken = rootState.user.token;

      const response = await axios.post(
        process.env.VUE_APP_API_URL + '/easter-egg/submit',
        { image: state.exportedImage, message: state.eggName || '' },
        { headers: { Authorization: `Bearer ${accessToken}` } },
      );

      commit('SET_SAVE_RESULT', response.data);
      // Use local base64 image instead of S3 URL to avoid replication delay
      const designWithLocalImage = { ...response.data, imageUrl: state.exportedImage };
      commit('SET_DESIGNS', [designWithLocalImage, ...state.designs]);
      return { success: true, data: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to save Easter egg';
      commit('SET_SAVE_ERROR', errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      commit('SET_SAVING', false);
    }
  },

  async fetchDesigns({ commit, rootState, state }) {
    try {
      commit('SET_DESIGNS_LOADING', true);

      const accessToken = rootState.user.token;

      const response = await axios.get(process.env.VUE_APP_API_URL + '/easter-egg/designs', {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      // Preserve local base64 images for recently saved designs (S3 may not have replicated yet)
      const localImages = {};
      state.designs.forEach(d => {
        if (d.imageUrl?.startsWith('data:')) localImages[d.id] = d.imageUrl;
      });
      const merged = response.data.map(d =>
        localImages[d.id] ? { ...d, imageUrl: localImages[d.id] } : d,
      );

      commit('SET_DESIGNS', merged);
      return { success: true, data: merged };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to fetch designs';
      return { success: false, error: errorMessage };
    } finally {
      commit('SET_DESIGNS_LOADING', false);
    }
  },

  async deleteDesign({ commit, rootState }, id) {
    try {
      const accessToken = rootState.user.token;

      await axios.delete(process.env.VUE_APP_API_URL + `/easter-egg/designs/${id}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      commit('REMOVE_DESIGN', id);
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to delete design';
      return { success: false, error: errorMessage };
    }
  },

  async renameDesign({ commit, rootState }, { id, message }) {
    try {
      const accessToken = rootState.user.token;
      const trimmedMessage = message.trim();

      await axios.patch(
        process.env.VUE_APP_API_URL + `/easter-egg/designs/${id}`,
        { message: trimmedMessage },
        { headers: { Authorization: `Bearer ${accessToken}` } },
      );

      commit('UPDATE_DESIGN_MESSAGE', { id, message: trimmedMessage });
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to rename design';
      return { success: false, error: errorMessage };
    }
  },

  async toggleFeatured({ commit, rootState }, { id, featured }) {
    try {
      const accessToken = rootState.user.token;
      await axios.patch(
        process.env.VUE_APP_API_URL + `/easter-egg/designs/${id}/featured`,
        { featured },
        { headers: { Authorization: `Bearer ${accessToken}` } },
      );
      commit('UPDATE_DESIGN_FEATURED', { id, featured });
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to update featured status';
      return { success: false, error: errorMessage };
    }
  },

  resetEasterEgg({ commit }) {
    commit('RESET_STATE');
  },
};

const getters = {
  hasSavedDesigns(state) {
    return state.designs.length > 0;
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
